import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { HyperSession, ModalType } from '../types/session';
import type { AppUserRole } from '../types/roles';
import { logAnalytics } from '../utils/database';
import { supabase } from '../utils/supabaseStorage';

interface AppState {
  session: HyperSession | null;
  /** Loaded from `public.users.role` for the signed-in auth user. */
  userRole: AppUserRole | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  activeModal: ModalType;
  error: string | null;
  errorModalTitle: string | null;
  errorModalMessage: string | null;
  requiredCredits: number | null;
  selectedProjectId: string | null;
}

interface AppStore extends AppState {
  setSession: (session: HyperSession | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  openModal: (modal: ModalType) => void;
  closeModal: () => void;
  openInsufficientCreditsModal: () => void;
  openInsufficientCreditsModalWithAmount: (credits: number) => void;
  openErrorModal: (title?: string, message?: string) => void;
  setSelectedProjectId: (projectId: string | null) => void;
  logout: () => void;
}

export const useAppStore = create<AppStore>()(
  persist(
    (set) => ({
      session: null,
      userRole: null,
      isAuthenticated: false,
      isLoading: false,
      activeModal: null,
      error: null,
      errorModalTitle: null,
      errorModalMessage: null,
      requiredCredits: null,
      selectedProjectId: null,

      setSession: async (session) => {
        if (session) {
          void logAnalytics('session_established', {
            user_id: session.hy_user_id,
            authenticated: true,
            sync_timestamp: session.synced_at,
          }).catch(() => {});

          let userRole: AppUserRole = 'user';
          try {
            const timeoutMs = 10_000;
            const rolePromise = supabase
              .from('users')
              .select('role')
              .eq('hy_user_id', session.hy_user_id)
              .maybeSingle();
            const outcome = await Promise.race([
              rolePromise,
              new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
            ]);
            if (outcome && typeof outcome === 'object' && 'data' in outcome) {
              const { data: row, error } = outcome as { data: { role?: string } | null; error: Error | null };
              if (error) {
                console.warn('[useAppStore] users.role fetch:', error.message);
              } else {
                const r = row?.role as string | undefined;
                if (r === 'superadmin' || r === 'admin' || r === 'creator' || r === 'user') {
                  userRole = r;
                }
              }
            } else {
              console.warn('[useAppStore] users.role fetch timed out; defaulting role to user');
            }
          } catch (e) {
            console.warn('[useAppStore] role resolution failed, defaulting to user', e);
          }

          set({
            session,
            userRole,
            isAuthenticated: true,
            error: null,
          });
        } else {
          set({
            session: null,
            userRole: null,
            isAuthenticated: false,
          });
        }
      },

      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      openModal: (activeModal) => set({ activeModal }),
      closeModal: () => set({ 
        activeModal: null,
        errorModalTitle: null,
        errorModalMessage: null,
        requiredCredits: null
      }),
      
      openInsufficientCreditsModal: () => set({ activeModal: 'insufficientCredits' }),
      openInsufficientCreditsModalWithAmount: (credits) => set({ 
        activeModal: 'insufficientCredits',
        requiredCredits: credits
      }),
      openErrorModal: (title, message) => set({
        activeModal: 'error',
        errorModalTitle: title || 'Something went wrong',
        errorModalMessage: message || 'At HYPER AI - fast, accurate, safe and secure is everything. the file you selected is not according to our community guidelines. please select/upload different file and try again'
      }),

      setSelectedProjectId: (projectId) => set({ selectedProjectId: projectId }),

      logout: async () => {
        await supabase.auth.signOut();
        localStorage.removeItem('hyper_fingerprint');
        set({
          session: null,
          userRole: null,
          isAuthenticated: false,
          activeModal: null,
          error: null,
          selectedProjectId: null,
        });
      },
    }),
    {
      name: 'hyper-app-store',
      partialize: (state) => ({
        selectedProjectId: state.selectedProjectId,
      }),
    }
  )
);